import { useCallback, useMemo, useState } from 'react'
import { fetchNotes, saveNote } from '../services/residentApi'
import type { ResidentNote } from '../types'
import type { PublicNoteRow } from './usePublicNotesAndMessages'

function parseSortTime(value: unknown): number {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000
  const n = Number(value)
  if (!Number.isNaN(n) && String(value).trim().length <= 10) return n * 1000
  const d = Date.parse(String(value))
  return Number.isNaN(d) ? 0 : d
}

/**
 * Liberty Internal tab — mirrors Angular `loadNotes` + `processNotesandMessage`:
 * GET `/api/notes/:id?source=INTERNAL&notes_for=DEFAULT` merged with
 * `residentDetail.insurance_message` rows (Angular `processNotesandMessage`).
 */
export function useResidentNotes(
  defaultId?: string | number,
  insuranceMessage?: ResidentNote[],
) {
  const [internalApiNotes, setInternalApiNotes] = useState<unknown[]>([])
  const [loading, setLoading] = useState(false)

  const loadNotes = useCallback(async (options?: { silent?: boolean }) => {
    if (!defaultId) return
    if (!options?.silent) setLoading(true)
    try {
      const response = await fetchNotes(defaultId, { source: 'INTERNAL', notes_for: 'DEFAULT' })
      const arr = Array.isArray(response)
        ? response
        : response && typeof response === 'object' && 'data' in (response as object)
          ? (response as { data: unknown[] }).data
          : []
      setInternalApiNotes(Array.isArray(arr) ? arr : [])
    } finally {
      if (!options?.silent) setLoading(false)
    }
  }, [defaultId])

  const notes = useMemo(() => {
    const rows: PublicNoteRow[] = []
    for (const n of internalApiNotes) {
      const row =
        typeof n === 'object' && n !== null ? ({ ...(n as object) } as PublicNoteRow) : ({} as PublicNoteRow)
      row.type = 'notes'
      row.content = String(row.notes ?? row.message ?? row.content ?? '')
      rows.push(row)
    }
    if (insuranceMessage?.length) {
      for (const m of insuranceMessage) {
        const row = { ...(m as object) } as PublicNoteRow
        if (!row.type) row.type = 'message'
        row.content = String(row.message ?? row.notes ?? row.content ?? '')
        rows.push(row)
      }
    }
    rows.sort((a, b) => parseSortTime(b.created_at) - parseSortTime(a.created_at))
    return rows
  }, [internalApiNotes, insuranceMessage])

  const addNote = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!defaultId) return
      await saveNote(defaultId, {
        ...payload,
        source: 'INTERNAL',
        notes_for: 'DEFAULT',
      })
      await loadNotes()
    },
    [defaultId, loadNotes],
  )

  return { notes, loading, loadNotes, addNote }
}
